import { isFcrActivated } from './deliverySummaryV1';
import { marketDetail, scheduledMw } from './marketReportsV1';

/**
 * @typedef {object} DeliveryChartRow
 * One plotted point of the 24 h / 7 d delivery chart.
 * @property {number} t period end, ms
 * @property {number|null} committed the period's commitment, kW; the one `committedPower`, or the
 *   market's own scheduled power when `toRows` is given a market; null where the period carried
 *   no market report
 * @property {number|null} availMean mean available power, kW
 * @property {number|null} availMin minimum available power over the period, kW
 * @property {[number, number]|null} band availability min/max, kW
 * @property {number|null} score setpoint following score 0–100; the writer scores every
 *   measured period (a quiet period holding its zero setpoint scores high), so null only
 *   where the period was not measured
 * @property {number|null} enduranceH mean hours the reservoir behind this market's bid would
 *   have sustained it; null without a market, where the backend predates the per-market
 *   figures, or where the direction was not measured
 * @property {boolean} activated the FCR service was activated in this period — the market's own
 *   `fcrActivatedDuration > 0` when the rows are built per market and the wire carries it,
 *   the battery-wide `isCalled` otherwise; never true off a measured period
 * @property {'measured'|'out_of_service'|'unmeasured'|null} state null on a gap row
 * @property {[number, number]|null} span the period the point covers, `[start, end]` ms; null on a gap row
 */

// MW → kW, one decimal. The only unit change the chart performs, done once on both the
// commitment and the availability so the comparison the panel exists for stays meaningful.
const KW = mw => Math.round(mw * 1000 * 10) / 10;

const gapRow = t => ({
    t,
    committed: null,
    availMean: null,
    availMin: null,
    band: null,
    score: null,
    enduranceH: null,
    activated: false,
    state: null,
    span: null,
});

/**
 * One report → one point. Settled reports are emitted one period apart, so a wider spacing is
 * a hole in the data rather than a trend; each hole gets a null row so the chart breaks its
 * line instead of drawing a confident flat segment across it.
 *
 * With a `market`, `committed` is that market's own scheduled power rather than the period's
 * `committedPower` — the per-market view the split delivery charts draw. Availability and the
 * score are battery-wide on the wire and stay the same whichever market is asked for.
 *
 * @param {import('./marketReportsV1').Delivered[]} reports
 * @param {{key: string, label: string, color: string, field: string}|null} [market]
 * @returns {DeliveryChartRow[]}
 */
export const toRows = (reports, market = null) => {
    const points = reports
        .map(r => {
            const start = Date.parse(r.start);
            const end = Date.parse(r.end);
            const m = r.markets;
            // The score is a genuine measure on every measured period — the writer scores a
            // quiet period against its zero setpoint too — so it is only withheld where the
            // period was not measured at all.
            const rawScore = r.measurement === 'measured' ? r.setpointFollowingScore : null;
            // The per-market figures, when the rows are built for one market and the
            // backend already sends them; null degrades to the battery-wide view.
            const detail = market === null ? null : marketDetail(r, market);
            return {
                t: end,
                periodMs: Math.max(1, end - start),
                committed: m === null ? null : KW(market === null ? m.committedPower : scheduledMw(m, market)),
                availMean: m === null || m.availablePowerMean === null ? null : KW(m.availablePowerMean),
                // Floored like the band: availability cannot be negative.
                availMin: m === null || m.availablePowerMin === null ? null : Math.max(0, KW(m.availablePowerMin)),
                // Availability cannot be negative; floored so a rounding artefact cannot push
                // the axis under the baseline.
                band:
                    m === null || m.availablePowerMin === null || m.availablePowerMax === null
                        ? null
                        : [Math.max(0, KW(m.availablePowerMin)), KW(m.availablePowerMax)],
                score: rawScore === null ? null : Math.round(rawScore * 1000) / 10,
                enduranceH: detail === null ? null : detail.enduranceMeanH,
                activated: isFcrActivated(r, market === null ? [] : [market]),
                state: r.measurement,
                span: [start, end],
            };
        })
        .sort((a, b) => a.t - b.t);

    const rows = [];
    points.forEach((p, i) => {
        const prev = points[i - 1];
        if (prev && p.t - prev.t > 2 * p.periodMs) {
            rows.push(gapRow((prev.t + p.t) / 2));
        }
        rows.push({
            t: p.t,
            committed: p.committed,
            availMean: p.availMean,
            availMin: p.availMin,
            band: p.band,
            score: p.score,
            enduranceH: p.enduranceH,
            activated: p.activated,
            state: p.state,
            span: p.span,
        });
    });
    return rows;
};

/**
 * The stretches to shade: consecutive rows in the same non-measured state, merged where their
 * spans touch. A measured row between two out-of-service rows leaves a hole — the shading
 * never bridges a period the battery actually ran. Gap rows carry no span and are skipped.
 *
 * @param {DeliveryChartRow[]} rows
 * @returns {Array<{state: 'out_of_service'|'unmeasured', from: number, to: number}>}
 */
export const stateSpans = rows => {
    const out = [];
    for (const row of rows) {
        if (row.span === null || row.state === null || row.state === 'measured') {
            continue;
        }
        const [from, to] = row.span;
        const last = out[out.length - 1];
        if (last && last.state === row.state && from <= last.to) {
            last.to = Math.max(last.to, to);
        } else {
            out.push({ state: row.state, from, to });
        }
    }
    return out;
};

/**
 * Every row's state as a contiguous span, measured included — the operating-status strip's
 * input. Same merge rule as `stateSpans`; a gap between reports stays a gap.
 *
 * @param {DeliveryChartRow[]} rows
 * @returns {Array<{state: 'measured'|'out_of_service'|'unmeasured', from: number, to: number}>}
 */
export const serviceSpans = rows => {
    const out = [];
    for (const row of rows) {
        if (row.span === null || row.state === null) {
            continue;
        }
        const [from, to] = row.span;
        const last = out[out.length - 1];
        if (last && last.state === row.state && from <= last.to) {
            last.to = Math.max(last.to, to);
        } else {
            out.push({ state: row.state, from, to });
        }
    }
    return out;
};

/**
 * The stretches to wash as activated: consecutive rows in which the FCR service was
 * activated, merged where their spans touch. A quiet or non-measured row carries no wash and
 * breaks a run; gap rows carry no span and are skipped.
 *
 * @param {DeliveryChartRow[]} rows
 * @returns {Array<{from: number, to: number}>}
 */
export const activatedSpans = rows => {
    const out = [];
    for (const row of rows) {
        if (row.span === null || !row.activated) {
            continue;
        }
        const [from, to] = row.span;
        const last = out[out.length - 1];
        if (last && from <= last.to) {
            last.to = Math.max(last.to, to);
        } else {
            out.push({ from, to });
        }
    }
    return out;
};
